import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Truck, Calendar, MapPin, Clock, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TowingConfirmationPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');
  const requestId = searchParams.get('request_id');

  useEffect(() => {
    if (sessionId && requestId) {
      confirmPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, requestId]);

  const confirmPayment = async () => {
    try {
      const response = await axios.get(
        `${API}/towing/confirm-payment?session_id=${sessionId}&request_id=${requestId}`
      );
      
      if (response.data.status === 'confirmed') {
        setRequest(response.data.request);
      } else {
        setError('Le paiement est en cours de traitement...');
      }
    } catch (err) {
      setError('Erreur lors de la confirmation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-zinc-400">Confirmation du paiement...</p>
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="mx-auto text-yellow-500 mb-4" size={48} />
            <h2 className="text-xl font-bold text-white mb-2">{error}</h2>
            <p className="text-zinc-400 mb-6">Veuillez patienter ou contacter le support.</p>
            <Link to="/mon-compte">
              <Button className="bg-orange-600 hover:bg-orange-700">
                Mon compte
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Demande confirmée !</h1>
              <p className="text-zinc-400">
                Votre demande de dépannage a été enregistrée avec succès.
              </p>
            </div>

            {/* Request Details */}
            {request && (
              <div className="bg-zinc-800/50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <Truck className="text-orange-400" size={24} />
                  <div>
                    <p className="text-zinc-400 text-sm">Type d'intervention</p>
                    <p className="text-white font-medium">
                      {request.intervention_type === 'remorquage' ? 'Remorquage' : 'Dépannage sur place'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="text-orange-400" size={24} />
                  <div>
                    <p className="text-zinc-400 text-sm">Date et heure</p>
                    <p className="text-white font-medium">
                      {new Date(request.preferred_date).toLocaleDateString('fr-FR', {
                        weekday: 'long', day: 'numeric', month: 'long'
                      })} à {request.preferred_time}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <MapPin className="text-orange-400" size={24} />
                  <div>
                    <p className="text-zinc-400 text-sm">Lieu d'intervention</p>
                    <p className="text-white font-medium">{request.location}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Clock className="text-orange-400" size={24} />
                  <div>
                    <p className="text-zinc-400 text-sm">Annulation gratuite jusqu'au</p>
                    <p className="text-white font-medium">
                      {new Date(request.cancellation_deadline).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>

                <hr className="border-zinc-700" />

                <div className="text-center">
                  <p className="text-zinc-400 text-sm mb-1">Acompte payé</p>
                  <p className="text-2xl font-bold text-green-500">{request.deposit_amount}€</p>
                  <p className="text-zinc-500 text-xs mt-1">Sera déduit de votre facture finale</p>
                </div>
              </div>
            )}

            {/* Info */}
            <div className="mt-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <p className="text-orange-400 text-sm">
                <AlertTriangle size={14} className="inline mr-1" />
                Un membre de notre équipe vous contactera pour confirmer l'intervention.
              </p>
            </div>

            {/* Actions */}
            <div className="mt-8 flex gap-4">
              <Link to="/mon-compte" className="flex-1">
                <Button className="w-full bg-orange-600 hover:bg-orange-700">
                  Voir mes demandes
                </Button>
              </Link>
              <Link to="/" className="flex-1">
                <Button variant="outline" className="w-full border-zinc-700">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
